// FT-191: трекер неактивности общего компьютера (только веб). Пишет метку последнего действия
// человека в localStorage (её видят все вкладки), раз в 5 секунд решает, не пора ли блокировать
// и не нужен ли keepalive-пинг, и принимает события других вкладок (протокол deviceTabs).
import { Capacitor } from "@capacitor/core";
import axios from "axios";
import {
  reloadIfBootedLocked, LAST_ACTIVITY_KEY, readLastActivity, markActivity, isIdleExpired, idleSeconds, needsKeepalivePing, isLockHotkey,
  activityCountable, currentIdleMinutes,
} from "@/utils/deviceLock";
import { parseDeviceEvent, freezePersistence } from "@/utils/deviceTabs";
import { disconnect as disconnectCable } from "@/cable";

const ACTIVITY_EVENTS = [ "mousemove", "mousedown", "keydown", "touchstart", "wheel" ];
const TICK_MS = 5000;
// Чаще не нужно: таймаут — в минутах, тик — раз в 5 с; каждая запись будит storage-событие во всех вкладках.
const ACTIVITY_WRITE_MS = 5000;
const SWITCH_READY_FALLBACK_MS = 15000;

let lastHeaderSentMs = null;
let lastWriteMs = 0;

export function currentIdleSeconds(now = Date.now()) {
  return idleSeconds(now, readLastActivity());
}

export function noteIdleHeaderSent(now = Date.now()) {
  lastHeaderSentMs = now;
}

// Сначала дешёвая проверка времени — обработчик висит на mousemove у всех веб-пользователей.
export function onActivity(store) {
  const now = Date.now();
  if (now - lastWriteMs < ACTIVITY_WRITE_MS) return;
  if (!activityCountable(store.state.deviceLock)) return;
  // Первое действие после простоя (сон компьютера, тик таймера не успел) — сначала блокировка,
  // иначе свежая метка отменила бы её (REQ-05).
  if (isIdleExpired(now, readLastActivity(), idleMinutes(store))) {
    store.dispatch("deviceLock/lock");
    return;
  }
  lastWriteMs = now;
  markActivity(window.localStorage, now);
}

function idleMinutes(store) {
  return currentIdleMinutes(store.getters["deviceLock/device"]);
}

function tick(store) {
  const s = store.state.deviceLock;
  if (!s.deviceMode || s.locked || s.tabClosed) return;
  const now = Date.now();
  const last = readLastActivity();
  if (isIdleExpired(now, last, idleMinutes(store))) {
    store.dispatch("deviceLock/lock");
    return;
  }
  if (needsKeepalivePing(now, last, lastHeaderSentMs)) {
    axios.post(`${store.getters.getApiUrl}/api/v1/trusted_device/ping`).catch(() => {});
  }
}

function onStorage(store, event) {
  if (event.key === LAST_ACTIVITY_KEY) return; // общая метка уже в хранилище
  const message = parseDeviceEvent(event);
  if (message) handleDeviceMessage(store, message);
}

// События других вкладок. Закрытая сменой сотрудника вкладка lock/unlock не принимает — иначе
// она подхватила бы токен нового сотрудника при своём сторе прежнего.
// Своя вкладка оказалась страницей прежнего сотрудника (401 при чужом токене в хранилище): закрыть
// её, как по событию switch; новый сотрудник уже загружен — «Обновить» доступна сразу.
export function closeStaleDeviceTab(store) {
  handleDeviceMessage(store, { type: "switch" });
  store.commit("deviceLock/SWITCH_READY");
}

export function handleDeviceMessage(store, message) {
  const s = store.state.deviceLock;
  if (message.type === "switch") {
    freezePersistence();
    store.commit("deviceLock/CLOSE_TAB");
    disconnectCable(); // сокет прежнего сотрудника закрыт без переподключения; случайный reconnect уйдёт без токена (cableToken в main.js)
    setTimeout(() => store.commit("deviceLock/SWITCH_READY"), SWITCH_READY_FALLBACK_MS);
    return;
  }
  if (message.type === "switch_ready") {
    store.commit("deviceLock/SWITCH_READY");
    return;
  }
  if (s.tabClosed) return;
  if (message.type === "lock") {
    store.dispatch("deviceLock/lockLocal", { userId: message.userId });
  } else if (message.type === "unlock" && message.userId === s.lockedUserId) {
    // Тот же сотрудник разблокировал в другой вкладке: берём его новый токен, страница остаётся.
    store.commit("ADOPT_TOKEN_LOCAL");
    store.commit("deviceLock/UNLOCK");
    reloadIfBootedLocked();
  }
}

export function startDeviceIdleTracker(store) {
  if (Capacitor.isNativePlatform()) return;
  ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, () => onActivity(store), { passive: true }));
  window.addEventListener("storage", (event) => onStorage(store, event));
  window.addEventListener("keydown", (event) => {
    const s = store.state.deviceLock;
    if (s.deviceMode && !s.locked && isLockHotkey(event)) {
      event.preventDefault();
      store.dispatch("deviceLock/lock");
    }
  });
  setInterval(() => tick(store), TICK_MS);
}
